// Auditoría de reglas: el historial de cada regla, junto y ordenado. El motor no tiene un historial
// global; los cambios de políticas y las reglas eliminadas no salen aquí.
import type { RetailCommission } from '@fiado/type-kit';

export interface AuditRow {
    key: string;
    at: string;
    action: string;
    performedBy: string;
    version: number | null;
    ruleId: string;
    ruleName: string;
}

export type AuditSortKey = 'at' | 'performedBy' | 'action' | 'rule';

export interface AuditSort {
    key: AuditSortKey;
    direction: 'asc' | 'desc';
}

export function mergeHistories(
    entries: ReadonlyArray<{
        rule: Pick<RetailCommission.CommissionRule, 'ruleId' | 'name'>;
        events: readonly RetailCommission.CommissionRuleEvent[];
    }>,
): AuditRow[] {
    return entries.flatMap(({ rule, events }) =>
        events.map((event, index) => ({
            key: `${rule.ruleId}-${event.at}-${String(index)}`,
            at: event.at,
            action: event.action,
            performedBy: event.performedBy,
            version: event.version,
            ruleId: rule.ruleId,
            ruleName: rule.name,
        })),
    );
}

const VALUE: Record<AuditSortKey, (row: AuditRow) => string> = {
    at: (row) => row.at,
    performedBy: (row) => row.performedBy,
    action: (row) => row.action,
    rule: (row) => row.ruleName,
};

export function sortAudit(rows: readonly AuditRow[], sort: AuditSort): AuditRow[] {
    const sign = sort.direction === 'asc' ? 1 : -1;
    const value = VALUE[sort.key];
    return [...rows].sort(
        (a, b) => sign * value(a).localeCompare(value(b)) || b.at.localeCompare(a.at),
    );
}

/** La fecha empieza descendente; las demás columnas, ascendente. Repetir la columna invierte. */
export function nextAuditSort(current: AuditSort, key: AuditSortKey): AuditSort {
    if (current.key === key)
        return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
    return { key, direction: key === 'at' ? 'desc' : 'asc' };
}
